import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { Typography, useMediaQuery } from '@mui/material';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import { reduxFormResolver } from '#helper/rhf-resolver';
import SelectProject from '#component/form/multiple-select-project';
import { isEnabledModuleById } from '#component/project/form/helper';
import { fetchSelectProjects, clearSelectProjects } from '#action/project/project-select';
import {
  FormContainer,
  StyledPaper,
  ProjectFieldWrapper,
  ButtonContainer,
  StyledButton
} from './styles';
import validate from './validate';
import { isProjectExpired } from '#helper';

/**
 * Renders warehouse react-hook-form fields.
 */

const Form = props => {
  const { warehouse, onSubmit, initialValues, pageDescription, history } = props;
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { projects, status } = useSelector(s => s.projectSelect, shallowEqual);
  const [hasProject, setHasProject] = useState(warehouse?.project ?? false);
  const SITE_MODULE_ID = 2;

  const methods = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: initialValues || {}
  });

  const { handleSubmit, setValue, formState: { isDirty, isSubmitting }, reset } = methods;

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const notExpiredProjects = useMemo(
    () =>
      projects.filter(project => !isProjectExpired(project)),
    [projects]
  );

  const projectsToSelect = useMemo(() => notExpiredProjects.map(project => ({
    label: project.title,
    value: project.id,
    disabled: !isEnabledModuleById(project.modules, SITE_MODULE_ID),
    disabledMsg: 'Enable module',
    ...project
  })), [notExpiredProjects]);

  const handleSetProject = project => {
    setValue('projectId', project.value, { shouldDirty: true });
    setHasProject(true);
  };

  const handleCancelClick = () => {
    if (isMobile || !location.key) {
      history.push('/warehouse');
    } else {
      history.goBack();
    }
  };

  const handleProjectsFetch = e => dispatch(fetchSelectProjects(e.target.value, true));

  const clearProjects = () => {
    dispatch(clearSelectProjects());
  };

  const fetchProjects = () => {
    dispatch(fetchSelectProjects(null, true));
  };

  return (
    <FormProvider {...methods}>
      <FormContainer autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h4" gutterBottom>
          {pageDescription}
        </Typography>
        <StyledPaper elevation={0}>
          <RHFTextField
            name="label"
            label="Label"
            variant="outlined"
            fullWidth
          />
          <ProjectFieldWrapper hasProject={hasProject}>
            <Controller
              name="projectId"
              control={methods.control}
              render={({ fieldState: { error } }) => (
                <SelectProject
                  label="Project"
                  placeholder="Project"
                  data={projectsToSelect}
                  setData={handleSetProject}
                  defaultData={warehouse?.project ?? []}
                  fetchProjects={handleProjectsFetch}
                  clearProjects={clearProjects}
                  loading={['loading'].includes(status)}
                  textFieldOnFocus={fetchProjects}
                  showDateRange={true}
                  meta={{
                    error: error?.message,
                    valid: !error,
                    submitFailed: !!error
                  }}
                />
              )}
            />
          </ProjectFieldWrapper>
        </StyledPaper>
        <ButtonContainer>
          <StyledButton
            variant="outlined"
            onClick={handleCancelClick}
          >
            Cancel
          </StyledButton>
          <StyledButton
            type="submit"
            variant="contained"
            color="secondary"
            disabled={!isDirty || isSubmitting}
          >
            Save
          </StyledButton>
        </ButtonContainer>
      </FormContainer>
    </FormProvider>
  );
};

export default Form;
